import React, { useEffect, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export interface Field {
  name: string;
  type: string;
  sampleValues: any[];
}

export interface FieldSelectorProps {
  label: string;
  pipelineId: string;
  nodeId: string;
  value?: string;
  onChange: (fieldName: string, field?: Field) => void;
  fieldTypes?: string[]; // Filter by type
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
}

export const FieldSelector: React.FC<FieldSelectorProps> = ({
  label,
  pipelineId,
  nodeId,
  value,
  onChange,
  fieldTypes,
  placeholder = 'Select field...',
  disabled = false,
  required = false,
  error,
}) => {
  const [fields, setFields] = useState<Field[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (!pipelineId || !nodeId) return;

    const fetchFields = async () => {
      setLoading(true);
      setFetchError(null);

      try {
        const response: any = await apiClient.get(
          `/pipelines/${pipelineId}/nodes/${nodeId}/fields`
        );

        // apiClient.get already returns response.data, so the JSON body is at
        // the top level — no extra .data nesting.
        setFields(response.fields || []);
      } catch (err: any) {
        console.error('Failed to fetch fields:', err);
        setFetchError(
          err.response?.data?.message || 'Failed to fetch available fields'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchFields();
  }, [pipelineId, nodeId]);

  // Filter fields by type if specified
  const filteredFields = fieldTypes
    ? fields.filter((f) => fieldTypes.includes(f.type))
    : fields;

  const selectedField = fields.find((f) => f.name === value);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {fetchError ? (
        <Alert variant="destructive">
          <AlertDescription>{fetchError}</AlertDescription>
        </Alert>
      ) : (
        <>
          <Select
            value={value || ''}
            onValueChange={(val) => {
              const field = fields.find((f) => f.name === val);
              onChange(val, field);
            }}
            disabled={disabled || loading || filteredFields.length === 0}
          >
            <SelectTrigger className={error ? 'border-red-500' : ''}>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {filteredFields.length === 0 ? (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  No fields available
                </div>
              ) : (
                filteredFields.map((field) => (
                  <SelectItem key={field.name} value={field.name}>
                    <div className="flex items-center justify-between gap-2">
                      <span>{field.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({field.type})
                      </span>
                    </div>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>

          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}

          {/* Field Preview */}
          {selectedField && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground cursor-help">
                    <Info className="h-4 w-4" />
                    <span>
                      {selectedField.name} ({selectedField.type})
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  <div className="space-y-1">
                    <p className="font-semibold">{selectedField.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Type: {selectedField.type}
                    </p>
                    {selectedField.sampleValues.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium">Sample values:</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedField.sampleValues.map((val, i) => (
                            <code
                              key={i}
                              className="text-xs bg-muted px-1 py-0.5 rounded"
                            >
                              {JSON.stringify(val)}
                            </code>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </>
      )}
    </div>
  );
};
